import { z } from 'zod'
import { decimalStringSchema, versionedRecordSchema } from './records.ts'

const nonnegative = decimalStringSchema.refine((value) => !value.startsWith('-'))
const positive = nonnegative.refine((value) => /[1-9]/.test(value))
export const productCreateSchema = z
  .object({
    name: z.string().trim().min(1).max(500),
    code: z.string().trim().min(1).max(200),
    kind: z.enum(['product', 'service']),
    unit: z.string().trim().min(1).max(40),
    basePrice: nonnegative,
  })
  .strict()
export const productUpdateSchema = versionedRecordSchema
  .extend({
    name: productCreateSchema.shape.name.optional(),
    code: productCreateSchema.shape.code.optional(),
    basePrice: nonnegative.optional(),
  })
  .strict()
export const catalogBomSchema = z
  .object({
    materialId: z.string().min(1),
    perUnit: positive,
    waste: positive.refine((value) => /[1-9]/.test(value.split('.')[0] ?? '')),
    operation: z.string().min(1).max(200),
  })
  .strict()
export const techCardUpdateSchema = versionedRecordSchema
  .extend({
    route: z.array(z.string().min(1)).max(100).optional(),
    bom: z.array(catalogBomSchema).max(500).optional(),
    instructions: z.string().max(100000).optional(),
    note: z.string().max(20000).nullable().optional(),
  })
  .strict()
export const techCardImportSchema = z
  .object({
    fileName: z.string().min(1).max(500),
    releaseFirst: z.literal(false),
    items: z
      .array(
        z
          .object({
            productId: z.string().min(1).nullable(),
            version: z.number().int().positive().optional(),
            create: productCreateSchema.omit({ kind: true }).nullable(),
            route: z.array(z.string().min(1)),
            bom: z.array(catalogBomSchema),
          })
          .strict(),
      )
      .min(1)
      .max(100),
  })
  .strict()
export type ProductCreateInput = z.infer<typeof productCreateSchema>
export type ProductUpdateInput = z.infer<typeof productUpdateSchema>
export type TechCardUpdateInput = z.infer<typeof techCardUpdateSchema>
export const techCardFileSchema = versionedRecordSchema.strict()
export const fileReplaceSchema = versionedRecordSchema
  .extend({
    fileId: z.string().min(1).max(200),
    comment: z.string().trim().max(500).nullable().optional(),
  })
  .strict()
export type FileReplaceInput = z.infer<typeof fileReplaceSchema>

export interface TechCardHistoryFile {
  id: string
  name: string
  revision: number
  comment: string | null
  sizeKb: number
  slot: 'files' | 'media'
}
interface HistoryBase {
  id: string
  at: string
  actorId: string
  cardVersion: number | null
}
export interface HistoryBomLine {
  materialId: string
  perUnit: string
  waste: string
  operation: string
}
export type TechCardHistoryEvent = HistoryBase &
  (
    | { kind: 'product_created' }
    | {
        kind: 'product_updated'
        changes: Array<{ field: 'name' | 'code' | 'basePrice'; from: string; to: string }>
      }
    | { kind: 'draft_started' | 'draft_imported' | 'draft_discarded' | 'archived' }
    | { kind: 'released'; note: string | null }
    | { kind: 'file_added' | 'file_removed'; file: TechCardHistoryFile }
    | {
        kind: 'file_replaced'
        file: TechCardHistoryFile
        previous: TechCardHistoryFile
      }
    | { kind: 'route_changed'; from: string[]; to: string[] }
    | { kind: 'materials_changed'; from: HistoryBomLine[] | null; to: HistoryBomLine[] }
    | { kind: 'instructions_changed' }
    | { kind: 'note_changed'; note: string | null }
  )
